import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plane, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { CITIES } from "../data/cities";
import { VEHICLES, computeFare } from "../data/pricing";
import { useLang } from "../i18n/LanguageContext";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { distanceFromAirport } from "../lib/googleMaps";
import { useSeo } from "../lib/seo";
import { usePricing } from "../lib/usePricing";
import Reveal from "../components/Reveal";
import BookingWidget from "../components/BookingWidget";

const PR = {
  de: { kicker: "Preise", title: "Flughafen Zürich Transfer-Preise — alle Ziele.", sub: "Transparente Fixpreise ab Flughafen Zürich (ZRH) für über 150 Ziele in der Schweiz und den Nachbarländern. Standardfahrzeug, Tagestarif.", calc: "Fahrpreis berechnen", calcSub: "Geben Sie Ihre Strecke ein und vergleichen Sie sofort den Preis jeder Fahrzeugklasse.", pickup: "Abholung", dest: "Ziel", destPh: "Flughafen, Ort oder Strasse …", empty: "Geben Sie oben einen Zielort ein, um die Preise zu sehen.", book: "Jetzt buchen", all: "Alle Ziele & Fixpreise" },
  en: { kicker: "Prices", title: "Zürich Airport transfer prices — all destinations.", sub: "Transparent fixed fares from Zürich Airport (ZRH) for over 150 destinations in Switzerland and neighbouring countries. Standard vehicle, daytime rate.", calc: "Calculate your fare", calcSub: "Enter your route and instantly compare the price for every vehicle class.", pickup: "Pickup", dest: "Destination", destPh: "Airport, city or street …", empty: "Enter a destination above to see the prices.", book: "Book now", all: "All destinations & fixed fares" },
  fr: { kicker: "Prix", title: "Prix des transferts aéroport de Zurich — toutes destinations.", sub: "Tarifs fixes transparents depuis l'aéroport de Zurich (ZRH) pour plus de 150 destinations en Suisse et dans les pays voisins. Véhicule standard, tarif de jour.", calc: "Calculer le prix", calcSub: "Saisissez votre trajet et comparez instantanément le prix de chaque classe.", pickup: "Départ", dest: "Destination", destPh: "Aéroport, ville ou rue …", empty: "Saisissez une destination ci-dessus pour voir les prix.", book: "Réserver", all: "Toutes les destinations & prix fixes" },
  it: { kicker: "Prezzi", title: "Prezzi transfer aeroporto di Zurigo — tutte le destinazioni.", sub: "Tariffe fisse trasparenti dall'aeroporto di Zurigo (ZRH) per oltre 150 destinazioni in Svizzera e nei paesi vicini. Veicolo standard, tariffa diurna.", calc: "Calcola la tariffa", calcSub: "Inserisci il percorso e confronta subito il prezzo di ogni classe.", pickup: "Partenza", dest: "Destinazione", destPh: "Aeroporto, città o via …", empty: "Inserisci una destinazione sopra per vedere i prezzi.", book: "Prenota ora", all: "Tutte le destinazioni & tariffe fisse" },
};

const tagFor = (c) => (c.canton === "INT" ? "INT" : c.canton === "FL" ? "FL" : null);

const Preise = () => {
  const { lang } = useLang();
  const p = PR[lang] || PR.de;
  const navigate = useNavigate();
  usePricing();
  useSeo({ title: `${p.title} | Airport Taxi Zürich`, description: p.sub, path: "/preise" });
  const [destText, setDestText] = useState("");
  const [dist, setDist] = useState(null);
  const [calc, setCalc] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const onSelect = async (place) => {
    setDestText(place.label); setCalc(true); setDist(null);
    const d = await distanceFromAirport({ lat: place.lat, lng: place.lng });
    setDist(d ? { ...d, label: place.label } : null); setCalc(false);
  };

  const sorted = useMemo(() => [...CITIES].sort((a, b) => a.name.localeCompare(b.name, "de")), []);

  return (
    <div className="pt-[68px] min-h-screen bg-white overflow-x-hidden" data-testid="prices-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <Reveal>
          <p className="font-body text-sm text-[#2B4BFF] font-semibold mb-3">{p.kicker}</p>
          <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0B1533]">{p.title}</h1>
          <p className="font-body text-base sm:text-lg text-[#5A607A] mt-5 max-w-2xl leading-relaxed">{p.sub}</p>
        </Reveal>

        <Reveal delay={60}>
          <BookingWidget className="mt-8" />
        </Reveal>

        {/* Fare calculator */}
        <Reveal delay={100}>
          <div className="mt-10 bg-[#0B1533] rounded-[28px] p-6 sm:p-8" data-testid="fare-calculator">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white">{p.calc}</h2>
            <p className="font-body text-sm text-white/60 mt-2 mb-6">{p.calcSub}</p>
            <div className="space-y-4">
              <div>
                <label className="font-body text-xs text-white/60 mb-1.5 block">{p.pickup}</label>
                <div className="relative"><Plane size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#2B4BFF]" /><input readOnly value="Flughafen Zürich (ZRH)" className="w-full bg-white/95 border border-transparent rounded-2xl pl-11 pr-3 py-3.5 font-body text-sm text-[#0B1533]" /></div>
              </div>
              <div>
                <label className="font-body text-xs text-white/60 mb-1.5 block">{p.dest}</label>
                <AddressAutocomplete value={destText} onChange={setDestText} onSelect={onSelect} placeholder={p.destPh} icon={MapPin} className="w-full bg-white/95 border border-transparent rounded-2xl pl-11 pr-3 py-3.5 font-body text-sm text-[#0B1533] focus:outline-none placeholder:text-[#9CA0B0]" />
              </div>
            </div>

            <div className="mt-6">
              {calc ? (
                <p className="font-body text-sm text-white/70 flex items-center gap-2 py-6 justify-center"><Loader2 size={16} className="animate-spin" /> …</p>
              ) : dist ? (
                <div data-testid="fare-results">
                  <div className="grid sm:grid-cols-2 gap-3">
                    {VEHICLES.map((v) => (
                      <div key={v.id} className="flex items-center justify-between bg-white/[0.06] border border-white/10 rounded-2xl px-4 py-3.5">
                        <div><span className="font-heading font-semibold text-white text-sm block">{v.name}</span><span className="font-body text-xs text-white/50">{v.seats} Sitze</span></div>
                        <span className="font-heading font-bold text-lg text-white">CHF {computeFare(v.id, dist.km)}.–</span>
                      </div>
                    ))}
                  </div>
                  <p className="font-body text-xs text-white/50 mt-4">≈ {dist.km} km · {dist.min} min · Flughafen Zürich → {dist.label}</p>
                  <button onClick={() => navigate(`/booking?to=${encodeURIComponent(dist.label)}`)} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full py-3.5 hover:bg-[#1E38D8] transition-colors" data-testid="calc-book-btn">{p.book} <ArrowRight size={18} /></button>
                </div>
              ) : (
                <p className="font-body text-sm text-white/50 border-t border-dashed border-white/15 pt-6 flex items-center gap-3 justify-center"><Plane size={18} className="text-[#2B4BFF]" /> {p.empty}</p>
              )}
            </div>
          </div>
        </Reveal>

        {/* All destinations list */}
        <div className="mt-14">
          <h2 className="font-heading font-semibold text-xl text-[#0B1533] mb-4">{p.all}</h2>
          <div className="rounded-2xl border border-[#E9E5DE] overflow-hidden" data-testid="prices-list">
            {sorted.map((c) => {
              const tag = tagFor(c);
              return (
                <button key={c.slug} onClick={() => navigate(c.path)} className="group w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-[#F0EDE7] last:border-b-0 hover:bg-[#F5F2ED] transition-colors text-left" data-testid={`price-row-${c.slug}`}>
                  <span className="flex items-center gap-3 min-w-0">
                    <Plane size={17} className="text-[#2B4BFF] shrink-0" />
                    <span className="font-body text-[15px] text-[#0B1533] truncate">{c.name}</span>
                    {tag && <span className="font-body text-[10px] font-semibold text-[#2B4BFF] bg-[#EEF1FF] rounded px-1.5 py-0.5 shrink-0">{tag}</span>}
                  </span>
                  <span className="font-heading font-bold text-[#0B1533] shrink-0">CHF {computeFare("economy", c.km)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preise;
